/** @jsx jsx */

import React, {
  Fragment as _,
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';
import { css, jsx } from '@emotion/core';
import { Box } from 'theme-ui';
import { interactionContext as InteractionContext } from './interactionContext';
import { AssessmentContext } from 'components/Module/AssessmentContainer';
import getBlockById from 'services/apiHelpers/getBlockById';
import { findByKeyVal_V2 } from 'services/findByKeyVal';
import getInteractionsInTopic from 'services/apiHelpers/getInteractionsInTopic';
import { useSelector, useDispatch } from 'react-redux';
import { State, QuestionData, Question } from 'store/store';
import { updateAssessmentAction } from 'actions/updateAssessmentAction';
import { Block } from '@chameleoncreator/creator-app';
import pubSub from 'services/pubSub';
import { colors } from 'styles/variables';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import EditMarker from 'components/UI/EditMarker';
import AssessmentCorrect from 'components/UI/AssessmentCorrect';
import AssessmentIncorrect from 'components/UI/AssessmentIncorrect';
import unlockInteraction from 'services/unlockInteraction';
import { ScoreBar } from './ScoreBar';
import { getQuestionsInAssessment } from './getQuestionsInAssessment';
import { Button, ReVisitTryAgain } from './Button';
import { getAttempt } from './getAttempt';
import { unlockAssessment } from './unlockAssessment';
import getCurrentTopic from 'services/apiHelpers/getCurrentTopic';

const { lPink, lGrey0, lGrey1, mint, purple } = colors;

type AttemptCount = Pick<QuestionData, 'attemptCount'>;

const AssessmentResults = ({ children, ...props }) => {
  const assessmentContext = useContext(AssessmentContext);
  const interactionContext = useContext<any>(InteractionContext);
  //
  const { assessmentId, learnMoreResetsAssessment = false } = assessmentContext;
  const assessment = getCurrentTopic() as Block;
  //

  const { id: interactionId } = interactionContext;
  const { graphColor = mint } = interactionContext.props;

  const correctColor = interactionContext.props.correctColor || mint;
  const incorrectColor = interactionContext.props.incorrectColor || purple;

  const {
    enableLearnMore,
    enableTryAgain = true,
    enableReVisit = true,
    includeResultsGraph = true,
    resultsPercentTextColor,
    attemptCounterTextColor,
    questionTitleColor = '#000',
    showAttemptCounter = true,
    showFeedbackText = false,
    addAssessmentRetry = true,
    //
    backgroundPanelColor = '#fff',
    backgroundPanelBorderRadius = 50,
    backgroundPanelDropShadow = true,

    buttonBorderRadius = 18,
  } = interactionContext.props;

  const [feedbackPassed, feedbackFailed] = children;

  const [showCorrect, setShowCorrect] = useState<boolean>(false);

  const assessmentData = useSelector((state: State) => {
    return state.assessments[assessmentId] || {};
    //
  });

  const { focusedNodeId } = useSelector((state: State) => {
    return state;
    //
  });

  const interactionsLocked = useSelector((state: State) => {
    return state.interactionsLocked;
  });

  const dispatch = useDispatch();

  // should we get this value off the assessment props now ?
  // since we're disabling the saving of assessmentData on reload ( ? )

  const { maxAttempts, passMark } = getBlockById(assessmentId).props as any;

  let numCorrect = 0;

  //
  // get all the question interactions in the current assessment
  // const assessmentBlock = getBlockById(assessmentId);
  // const assessmentInteractions = getInteractionsInTopic(assessmentBlock);

  const questions = getQuestionsInAssessment(assessmentId);
  //

  let results = questions.map((entry, index) => {
    let id;
    if (entry?.children) {
      id = (entry?.children[0] as Block)?.id;
    }

    return {
      id,
      questionName: entry?.props?.name,
      hasNotBeenAttempted: true,
    } as QuestionData;
  });

  if (assessmentData.results) {
    Object.keys(assessmentData.results).forEach((entry: any, index: number) => {
      const {
        id,
        questionName,
        answer,
        correctAnswer,
        isCorrect,
        attemptCount,
      } = assessmentData.results[entry];

      if (isCorrect) {
        numCorrect += 1;
      }

      const questionIndex = results.findIndex(q => {
        return id === q.id;
      });

      results[questionIndex] = {
        id,
        questionName,
        answer,
        correctAnswer,
        isCorrect,
        attemptCount,
      };
    });
  }

  let score = (numCorrect / results.length) * 100;

  if (window.dev && window.dev()) {
    score = showCorrect ? 100 : 0;
  }

  const attempt: number = getAttempt(maxAttempts, results);

  const allQuestionsMaxAttempted = attempt === maxAttempts;

  interactionContext.enableRetryAssessmentBtn = {
    enabled: addAssessmentRetry,
    visible:
      addAssessmentRetry &&
      !(score >= passMark || maxAttempts === 1 || allQuestionsMaxAttempted),
  };

  interactionContext.updatePassedFailed(score >= passMark);

  const resetQuestion = id => {
    const {
      questionName,
      answer,
      correctAnswer,
      isCorrect,
      attemptCount,
    } = assessmentData.results[id];

    dispatch(
      updateAssessmentAction({
        assessmentId,
        assessmentData: {
          ...assessmentData.data,
          retryAssessment: false,
        },
        questionData: {
          [id]: {
            id,
            questionName,
            answer: [],
            correctAnswer,
            isCorrect: undefined,
            attemptCount,
            submitButtonLabel: 'Submit',
            navigateToResults: true,
            isLocked: false,
          },
        },
      })
    );
  };

  // create a results object for dev. . .
  let resultsInDev;
  if (window.dev && window.dev()) {
    resultsInDev = questions.map((entry: Block, index: number) => {
      const { name, includeLearnMore, learnMoreLocation } = entry.props as any;

      return {
        id: entry.id,
        questionName: name || `Question ${index + 1}`,
        isCorrect: showCorrect,
        includeLearnMore,
        learnMoreLocation,
      };
    });
  }

  useEffect(() => {
    const pS = pubSub.subscribe(
      `RESULTS_SHOW_CORRECT_${interactionId}`,
      ({ showCorrect }) => {
        setShowCorrect(showCorrect);
      }
    );

    return () => {
      pS();
    };
  }, []);

  useEffect(() => {
    if (!interactionsLocked?.includes(interactionId)) {
      return;
    }

    if (score >= passMark || allQuestionsMaxAttempted) {
      unlockInteraction(interactionId);
    }
  });

  const getQuestions = () => {
    const assessmentBlock = getBlockById(assessmentId);
    const assessmentInteractions = getInteractionsInTopic(assessmentBlock);

    const questions = assessmentInteractions.filter((i: Block) => {
      return i.meta?.subType === 'AssessmentQuestion';
    });

    return questions;
  };

  return (
    <_>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%',
        }}
      >
        {includeResultsGraph && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              margin: '0 0 10px 0',
              color: resultsPercentTextColor,
            }}
          >
            <Box as="h2" sx={{ margin: '0 0 10px 0', textAlign: 'center' }}>
              You got {Math.round(score)}%
            </Box>
            <ScoreBar color={graphColor} score={Math.round(score)} />
          </Box>
        )}

        {showAttemptCounter && !isNaN(attempt) && (
          <Box
            sx={{
              margin: '0 0 5px 0',
              color: attemptCounterTextColor,
            }}
          >
            Attempt {attempt} of {maxAttempts}
          </Box>
        )}

        {showFeedbackText && (
          <div
            css={css({
              width: '80%',
              margin: '15px 0',
            })}
          >
            {score >= passMark ? feedbackPassed : feedbackFailed}
          </div>
        )}

        <Box
          sx={{
            width: '80%',
            margin: '20px 0',
            padding: '20px 40px',

            borderRadius: backgroundPanelBorderRadius,
            background: backgroundPanelColor,
            boxShadow: backgroundPanelDropShadow
              ? '0px 0px 6px 0px rgba(0,0,0,0.15)'
              : '',
            fontSize: '14px',
          }}
        >
          <Box>
            {(window.dev && window.dev() ? resultsInDev : results).map(
              (entry: any, indexA: number) => {
                //

                const {
                  id,
                  questionName,
                  isCorrect,
                  attemptCount,
                  hasNotBeenAttempted,
                } = entry;

                // ' assuming ' we're in sync re_ sequence
                // need to be DAMN sure. so check it a
                const question = questions.filter(
                  (question: Block, indexB: number) => {
                    return indexA === indexB;
                  }
                )[0];

                if (hasNotBeenAttempted) {
                  return (
                    <Box
                      key={`${questionName}_${indexA}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '35px',
                      }}
                    >
                      {questionName} has not been attempted
                    </Box>
                  );
                }

                let { includeLearnMore } = question.props as any;

                if (enableLearnMore) {
                  includeLearnMore = true;
                }

                const LearnMore = findByKeyVal_V2(
                  question.id,
                  'type',
                  'LearnMore'
                ) as Block;

                let learnMoreLocation = LearnMore?.props?.location;

                const hasUsedAllAttempts =
                  maxAttempts === 1 || attemptCount >= maxAttempts;

                return (
                  <Box
                    key={`${questionName}_${indexA}`}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      margin: '10px 0',

                      '.learn-more-revisit-try-again-container': {
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        '> *': {
                          height: '35px',
                          marginLeft: '20px',
                        },
                      },

                      '@media (max-width: 768px)': {
                        flexDirection: 'column',

                        svg: {
                          transform: 'scale(2)',
                          marginBottom: '20px',
                        },

                        '> *': {
                          marginLeft: '0px !important',
                          marginRight: '0px !important',
                          margin: '0 0 10px 0',
                        },

                        '.learn-more-revisit-try-again-container': {
                          '> *': {
                            marginLeft: '0px !important',
                            marginRight: '0px !important',
                            margin: '5px 10px !important',
                          },
                        },
                      },
                    }}
                  >
                    {isCorrect ? (
                      <AssessmentCorrect color={correctColor} />
                    ) : (
                      <AssessmentIncorrect color={incorrectColor} />
                    )}

                    <span
                      className="question-name"
                      style={{
                        flex: 2,
                        marginLeft: '20px',
                        fontWeight: 500,
                        color: questionTitleColor,
                      }}
                    >
                      {questionName}
                    </span>

                    <span
                      className="learn-more-revisit-try-again-container"
                      style={{}}
                    >
                      {includeLearnMore && !isCorrect && (
                        <span style={{}}>
                          <Button
                            color={incorrectColor}
                            textColor="#fff"
                            buttonBorderRadius={buttonBorderRadius}
                            onClick={() => {
                              if (window.dev && window.dev()) {
                                return;
                              }

                              if (learnMoreLocation) {
                                //
                                //

                                if (
                                  learnMoreResetsAssessment &&
                                  !hasUsedAllAttempts
                                ) {
                                  unlockAssessment(
                                    assessmentData,
                                    dispatch,
                                    updateAssessmentAction,
                                    assessment
                                  );
                                }
                                //
                                window.location.hash = learnMoreLocation;
                              }
                            }}
                          >
                            Learn more
                            {window.dev && window.dev() && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: '-5px',
                                  top: '-3px',
                                  width: 'calc(100% + 10px)',
                                  height: 'calc(100% + 6px)',
                                  boxShadow: `0 0 0 1px ${lPink}`,
                                  background: 'transparent',

                                  cursor: 'pointer',
                                  transition: 'box-shadow 0.2s',

                                  '&:hover': {
                                    boxShadow: `0 0 0 1px lightGrey`,
                                  },

                                  ...(focusedNodeId === question.id && {
                                    boxShadow: `0 0 0 1px lightGrey`,
                                    pointerEvents: 'none',
                                  }),
                                }}
                                onClick={() => {
                                  if (window.dev && window.dev()) {
                                    setCurrentBlock(LearnMore);
                                    return;
                                  }
                                }}
                              >
                                {focusedNodeId !== LearnMore.id && (
                                  <EditMarker />
                                )}
                              </Box>
                            )}
                          </Button>
                        </span>
                      )}

                      {maxAttempts > 1 && !isCorrect && enableTryAgain && (
                        <span
                          style={{
                            ...(window.dev &&
                              !window.dev() &&
                              !isCorrect &&
                              hasUsedAllAttempts && {
                                opacity: 0.2,
                                pointerEvents: 'none',
                              }),
                          }}
                        >
                          <ReVisitTryAgain
                            color={incorrectColor}
                            buttonBorderRadius={buttonBorderRadius}
                            resetQuestion={() => resetQuestion(id)}
                            indexA={indexA}
                          >
                            Try-again
                          </ReVisitTryAgain>
                        </span>
                      )}
                      {isCorrect && enableReVisit && (
                        <span style={{}}>
                          <ReVisitTryAgain
                            color={correctColor}
                            buttonBorderRadius={buttonBorderRadius}
                            indexA={indexA}
                          >
                            Re-visit
                          </ReVisitTryAgain>
                        </span>
                      )}
                    </span>

                    <Box
                      as="hr"
                      sx={{
                        display: 'none',
                        '@media (max-width: 768px)': {
                          width: '100%',
                          margin: '5px 0 10px 0',
                          border: '1px solid #CADCF2',
                          display: 'block',
                        },
                      }}
                    />
                  </Box>
                );
              }
            )}
          </Box>
        </Box>
      </Box>
    </_>
  );
};

export default AssessmentResults;
